<script setup lang="ts">
import { useClientStore } from '../stores/clients'
import { useInventoryStore } from '../stores/inventory'
import { useSaleStore } from '../stores/sales'
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import type { Sale, SaleItem } from '../types'
import { formatCurrency } from '../utils/format'
import { UiButton, UiCard, UiAlert, UiLoader, UiBadge } from '../components/ui'

const router = useRouter()
const route = useRoute()
const clientStore = useClientStore()
const inventoryStore = useInventoryStore()
const saleStore = useSaleStore()

const selectedClientId = ref('')
const clientSearch = ref('')
const clientAutocompleteOpen = ref(false)
const cartItems = ref<SaleItem[]>([])
const productSearch = ref('')
const discountPercent = ref(0)
const isEditMode = ref(false)
const saleId = ref<string | null>(null)
const pageLoading = ref(true)

const clients = computed(() => clientStore.clients)
const products = computed(() => inventoryStore.products)
const isLoading = computed(() => clientStore.isLoading || inventoryStore.isLoading || saleStore.isLoading)
const error = computed(() => clientStore.error || inventoryStore.error || saleStore.error)

const total = computed(() =>
  Math.max(0, subtotal.value - discountAmount.value)
)
const subtotal = computed(() =>
  cartItems.value.reduce((sum, item) => sum + (item.price * item.quantity), 0)
)
const discountAmount = computed(() => subtotal.value * Math.min(100, Math.max(0, Number(discountPercent.value) || 0)) / 100)

function normalize(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

const clientSuggestions = computed(() => {
  const query = normalize(clientSearch.value.trim())
  if (!query) return clients.value.slice(0, 8)
  return clients.value.filter(client => normalize(client.name).includes(query)).slice(0, 8)
})

const topProducts = computed(() => {
  const soldQuantities = new Map<string, number>()
  saleStore.sales.forEach(sale => sale.items.forEach(item => {
    soldQuantities.set(item.productId, (soldQuantities.get(item.productId) || 0) + item.quantity)
  }))
  return [...products.value]
    .sort((a, b) => (soldQuantities.get(b.id) || 0) - (soldQuantities.get(a.id) || 0))
    .slice(0, 5)
})

const productResults = computed(() => {
  const query = normalize(productSearch.value.trim())
  if (!query) return topProducts.value
  return products.value.filter(product =>
    normalize(product.name).includes(query) || normalize(product.reference || '').includes(query)
  )
})

onMounted(async () => {
  pageLoading.value = true
  const id = route.params.id as string
  const promises: Promise<unknown>[] = [clientStore.fetchClients(), inventoryStore.fetchProducts(), saleStore.fetchSales()]
  if (id) {
    isEditMode.value = true
    saleId.value = id
  }
  await Promise.all(promises)
  if (isEditMode.value && saleId.value) {
    const sale = saleStore.getSale(saleId.value)
    if (sale) {
      selectedClientId.value = sale.clientId
      clientSearch.value = clientStore.getClient(sale.clientId)?.name || ''
      cartItems.value = JSON.parse(JSON.stringify(sale.items))
      discountPercent.value = sale.discount || 0
    }
  }
  pageLoading.value = false
})

async function addToCart(productId: string) {
  const product = inventoryStore.getProduct(productId)
  if (!product || product.stock <= 0) return
  const existingItem = cartItems.value.find(item => item.productId === productId)
  if (existingItem) {
    existingItem.quantity++
  } else {
    cartItems.value.push({ productId, quantity: 1, price: product.price })
  }
  await inventoryStore.updateStock(productId, -1)
}

function selectClient(clientId: string) {
  selectedClientId.value = clientId
  clientSearch.value = clientStore.getClient(clientId)?.name || ''
  clientAutocompleteOpen.value = false
}

function clearClientSelection() {
  selectedClientId.value = ''
  clientSearch.value = ''
  clientAutocompleteOpen.value = true
}

function onClientSearch() {
  if (clientSearch.value !== clientStore.getClient(selectedClientId.value)?.name) {
    selectedClientId.value = ''
  }
  clientAutocompleteOpen.value = true
}

function closeClientAutocomplete() {
  window.setTimeout(() => clientAutocompleteOpen.value = false, 150)
}

async function decreaseCartItemQuantity(productId: string) {
  const existingItem = cartItems.value.find(item => item.productId === productId)
  if (!existingItem) return
  if (existingItem.quantity > 1) {
    existingItem.quantity--
    await inventoryStore.updateStock(productId, 1)
  } else {
    const itemIndex = cartItems.value.findIndex(item => item.productId === productId)
    if (itemIndex > -1) {
      await inventoryStore.updateStock(productId, 1)
      cartItems.value.splice(itemIndex, 1)
    }
  }
}

async function removeFromCart(index: number) {
  const item = cartItems.value[index]
  await inventoryStore.updateStock(item.productId, item.quantity)
  cartItems.value.splice(index, 1)
}

async function handleSubmit() {
  if (!selectedClientId.value || cartItems.value.length === 0) return
  if (isEditMode.value && saleId.value) {
    const saleData: Sale = {
      id: saleId.value,
      clientId: selectedClientId.value,
      date: new Date(),
      items: cartItems.value,
      discount: discountPercent.value,
      total: total.value,
    }
    await saleStore.updateSale(saleData)
  } else {
    const saleData: Omit<Sale, 'id'> = {
      clientId: selectedClientId.value,
      date: new Date(),
      items: cartItems.value,
      discount: discountPercent.value,
      total: total.value,
    }
    await saleStore.addSale(saleData)
  }
  if (!saleStore.error) {
    router.push('/sales')
  }
}
</script>

<template>
  <div class="page-container">
    <div class="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 class="text-xl font-bold text-surface-800 dark:text-surface-100 sm:text-3xl">
          {{ isEditMode ? 'Editar Venda' : 'Nova Venda' }}
        </h1>
        <p class="mt-1 text-xs text-surface-500 dark:text-surface-400 sm:text-sm">
          {{ isEditMode ? 'Altere os itens da venda' : 'Selecione os produtos para a venda' }}
        </p>
      </div>
      <UiButton variant="secondary" @click="router.push('/sales')">
        <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Voltar
      </UiButton>
    </div>

    <UiAlert variant="error" :message="error || ''" />
    <UiLoader v-if="pageLoading" text="Carregando dados..." />

    <div v-if="!pageLoading && !error" class="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div class="space-y-6 lg:col-span-2">
        <UiCard title="Cliente">
          <div class="relative">
            <label class="label" for="client-search">Buscar cliente</label>
            <div class="relative">
              <input
                id="client-search"
                v-model="clientSearch"
                class="input pr-10"
                placeholder="Digite qualquer parte do nome..."
                autocomplete="off"
                @focus="clientAutocompleteOpen = true"
                @input="onClientSearch"
                @blur="closeClientAutocomplete"
              />
              <button
                v-if="clientSearch"
                type="button"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600"
                aria-label="Limpar cliente"
                @mousedown.prevent="clearClientSelection"
              >
                <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div
              v-if="clientAutocompleteOpen"
              class="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-surface-200 bg-white py-1 shadow-lg dark:border-surface-700 dark:bg-surface-900"
            >
              <button
                v-for="client in clientSuggestions"
                :key="client.id"
                type="button"
                class="block w-full px-4 py-2.5 text-left text-sm transition-colors hover:bg-brand-50 dark:hover:bg-surface-800"
                @mousedown.prevent="selectClient(client.id)"
              >
                <span class="font-medium text-surface-800 dark:text-surface-100">{{ client.name }}</span>
                <span v-if="client.email" class="ml-2 text-xs text-surface-400">{{ client.email }}</span>
              </button>
              <p v-if="clientSuggestions.length === 0" class="px-4 py-3 text-sm text-surface-500">Nenhum cliente encontrado.</p>
            </div>
          </div>
        </UiCard>

        <UiCard title="Produtos">
          <div class="mb-5">
            <label class="label" for="product-search">Buscar produto</label>
            <input
              id="product-search"
              v-model="productSearch"
              class="input"
              placeholder="Digite o nome ou referência do produto..."
              autocomplete="off"
            />
            <p v-if="productSearch.trim() && productResults.length === 0" class="mt-3 text-sm text-surface-500 dark:text-surface-400">
              Nenhum produto encontrado para “{{ productSearch }}”.
            </p>
          </div>
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div
              v-for="product in productResults"
              :key="product.id"
              class="group rounded-xl border-2 p-4 transition-all duration-150"
              :class="product.stock <= 0
                ? 'border-surface-200 bg-surface-50 opacity-50 dark:border-surface-800 dark:bg-surface-900'
                : 'border-surface-200 hover:border-brand-200 hover:shadow-soft dark:border-surface-700 dark:hover:border-brand-700'"
            >
              <div class="flex flex-col justify-between h-full">
                <div>
                  <h3 class="font-semibold text-surface-800 dark:text-surface-100">{{ product.name }}</h3>
                  <p v-if="product.reference" class="mt-0.5 text-xs text-surface-400 dark:text-surface-500">Ref.: {{ product.reference }}</p>
                  <p v-if="product.description" class="mt-0.5 text-xs text-surface-400 dark:text-surface-500">{{ product.description }}</p>
                  <p class="mt-2 text-lg font-bold text-brand-600 dark:text-brand-400">{{ formatCurrency(product.price) }}</p>
                </div>
                <div class="mt-4 flex items-center justify-between">
                  <span
                    class="text-xs font-medium"
                    :class="product.stock <= 0 ? 'text-red-500' : 'text-surface-400 dark:text-surface-500'"
                  >
                    {{ product.stock <= 0 ? 'Sem estoque' : `${product.stock} em estoque` }}
                  </span>
                  <UiButton
                    v-if="product.stock > 0"
                    size="sm"
                    @click="addToCart(product.id)"
                    :disabled="isLoading"
                  >
                    <svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    Adicionar
                  </UiButton>
                  <UiBadge v-else variant="red" label="Indisponível" />
                </div>
              </div>
            </div>
          </div>
        </UiCard>
      </div>

      <div class="lg:sticky lg:top-6 lg:h-fit">
        <UiCard title="Carrinho">
          <div v-if="cartItems.length === 0" class="flex flex-col items-center py-8 text-center">
            <div class="mb-3 rounded-full bg-surface-100 dark:bg-surface-800 p-3">
              <svg class="h-6 w-6 text-surface-400 dark:text-surface-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
              </svg>
            </div>
            <p class="text-sm font-medium text-surface-500 dark:text-surface-400">Carrinho vazio</p>
            <p class="mt-0.5 text-xs text-surface-400 dark:text-surface-500">Adicione produtos ao carrinho</p>
          </div>

          <div v-else class="divide-y divide-surface-100 dark:divide-surface-800">
            <div
              v-for="(item, index) in cartItems"
              :key="item.productId"
              class="flex items-center justify-between py-3"
            >
              <div class="min-w-0 flex-1 pr-4">
<p class="truncate text-sm font-medium text-surface-800 dark:text-surface-100">
                   {{ inventoryStore.getProduct(item.productId)?.name }}
                 </p>
                 <p class="mt-0.5 text-xs text-surface-400 dark:text-surface-500">
                   {{ item.quantity }}x {{ formatCurrency(item.price) }}
                 </p>
              </div>
              <div class="flex shrink-0 items-center gap-1">
                <button
                  @click="decreaseCartItemQuantity(item.productId)"
                  :disabled="isLoading"
                  class="flex h-7 w-7 items-center justify-center rounded-lg text-surface-500 dark:text-surface-400 transition-colors hover:bg-surface-100 hover:text-surface-700 dark:hover:bg-surface-800 dark:hover:text-surface-200 disabled:opacity-40"
                >
                  <svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M20 12H4" />
                  </svg>
                </button>
                <span class="w-6 text-center text-sm font-medium text-surface-800 dark:text-surface-100">{{ item.quantity }}</span>
                <button
                  @click="addToCart(item.productId)"
                  :disabled="isLoading || (inventoryStore.getProduct(item.productId)?.stock ?? 0) <= 0"
                  class="flex h-7 w-7 items-center justify-center rounded-lg text-surface-500 dark:text-surface-400 transition-colors hover:bg-surface-100 hover:text-surface-700 dark:hover:bg-surface-800 dark:hover:text-surface-200 disabled:opacity-40"
                >
                  <svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                </button>
                <button
                  @click="removeFromCart(index)"
                  :disabled="isLoading"
                  class="ml-1 flex h-7 w-7 items-center justify-center rounded-lg text-red-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400 disabled:opacity-40"
                >
                  <svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div v-if="cartItems.length > 0" class="mt-4 space-y-4 border-t pt-4">
            <div>
              <label class="label" for="discount-percent">Desconto (%)</label>
              <div class="relative">
                <input
                  id="discount-percent"
                  v-model.number="discountPercent"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  class="input pr-8"
                  placeholder="0"
                />
                <span class="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-surface-400">%</span>
              </div>
            </div>
            <div class="space-y-1.5 text-sm">
              <div class="flex items-center justify-between text-surface-500 dark:text-surface-400">
                <span>Subtotal</span>
                <span>{{ formatCurrency(subtotal) }}</span>
              </div>
              <div v-if="discountAmount > 0" class="flex items-center justify-between text-green-600 dark:text-green-400">
                <span>Desconto ({{ Math.min(100, Math.max(0, Number(discountPercent) || 0)) }}%)</span>
                <span>- {{ formatCurrency(discountAmount) }}</span>
              </div>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-surface-500 dark:text-surface-400">Total</span>
              <span class="text-xl font-bold text-surface-800 dark:text-surface-100">{{ formatCurrency(total) }}</span>
            </div>
            <UiButton
              class="w-full"
              :disabled="!selectedClientId || cartItems.length === 0 || isLoading"
              :loading="isLoading"
              @click="handleSubmit"
            >
              {{ isEditMode ? 'Atualizar Venda' : 'Finalizar Venda' }}
            </UiButton>
          </div>
        </UiCard>
      </div>
    </div>
  </div>
</template>
